import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { CHECKLIST_DATA } from '../data/checklistData';
import { API_CONFIG } from '../config/config';

const ChecklistContext = createContext();

export const ChecklistProvider = ({ children }) => {
  const { user } = useAuth();
  const [quizResult, setQuizResult] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load quiz result from database only (no localStorage) on mount and when user changes
  useEffect(() => {
    const loadQuizResult = async () => {
      if (user) {
        // Clear any existing localStorage quiz results for security
        // Remove all quiz-related localStorage items to prevent cross-user data leakage
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('quiz_result_') || key.startsWith('quiz_completed_'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));

        try {
          const token = localStorage.getItem('token');
          if (token) {
            const response = await fetch(`${API_CONFIG.BASE_URL}/quiz-results/get`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });

            if (response.ok) {
              const dbResult = await response.json();
              if (dbResult && dbResult.result_data) {
                // Parse the full result data from database
                const parsedResult = JSON.parse(dbResult.result_data);
                setQuizResult(parsedResult);
                return;
              }
            }
          }
          // No result found in database
          setQuizResult(null);
        } catch (error) {
          console.error('Error loading quiz result from database:', error);
          setQuizResult(null);
        }
      } else {
        // User logged out - clear quiz result
        setQuizResult(null);
        // Also clear any remaining quiz-related localStorage items
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('quiz_result_') || key.startsWith('quiz_completed_'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }
    };

    loadQuizResult();
  }, [user]);

  // Load checklist items from database or initialize with default data
  useEffect(() => {
    const loadChecklistProgress = async () => {
      if (user) {
        // Initialize with default data (only Basissicherheit)
        const defaultItems = CHECKLIST_DATA.map(cat => ({
          ...cat,
          items: cat.items.map(item => ({ ...item }))
        }));

        try {
          const token = localStorage.getItem('token');
          if (token) {
            const response = await fetch(`${API_CONFIG.BASE_URL}/checklist-progress/get`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });

            if (response.ok) {
              const dbProgress = await response.json();
              if (dbProgress && dbProgress.progress_data) {
                try {
                  const parsed = JSON.parse(dbProgress.progress_data);
                  
                  // Handle new format with checklistItems and markedAsDone
                  let checklistData = parsed;
                  let markedAsDone = {};
                  
                  if (parsed.checklistItems && parsed.markedAsDone) {
                    // New format
                    checklistData = parsed.checklistItems;
                    markedAsDone = parsed.markedAsDone || {};
                  } else if (Array.isArray(parsed)) {
                    // Old format (just array of categories)
                    checklistData = parsed;
                  }
                  
                  // Filter to only Basissicherheit category and migrate old data
                  const basissicherheitCategory = checklistData.find(cat => cat.category === 'Basissicherheit');
                  
                  if (basissicherheitCategory) {
                    // Use existing Basissicherheit data, but ensure only valid module IDs are included
                    const validModuleIds = [1, 2, 3, 4, 7, 11, 13, 15, 16, 18];
                    const filteredItems = basissicherheitCategory.items.filter(item => 
                      validModuleIds.includes(item.id)
                    );
                    
                    // If we have valid items, use them; otherwise use default
                    if (filteredItems.length > 0) {
                      setChecklistItems([{
                        ...basissicherheitCategory,
                        items: filteredItems
                      }]);
                      
                      // Store markedAsDone in a way that can be accessed by components
                      // We'll use a custom event or context extension for this
                      // For now, we'll store it in localStorage as a fallback
                      if (user && Object.keys(markedAsDone).length > 0) {
                        const markedKey = `marked_as_done_${user.id}`;
                        localStorage.setItem(markedKey, JSON.stringify(markedAsDone));
                      }
                      
                      setIsLoaded(true);
                      return;
                    }
                  }
                } catch (e) {
                  console.error('Error parsing checklist progress from database:', e);
                }
              }
            }
          }
          
          // No progress found in database or error occurred, use default
          setChecklistItems(defaultItems);
          setIsLoaded(true);
        } catch (error) {
          console.error('Error loading checklist progress from database:', error);
          // On error, use default data
          const defaultItems = CHECKLIST_DATA.map(cat => ({
            ...cat,
            items: cat.items.map(item => ({ ...item }))
          }));
          setChecklistItems(defaultItems);
          setIsLoaded(true);
        }
      } else {
        setChecklistItems([]);
        setIsLoaded(false);
      }
    };

    loadChecklistProgress();
  }, [user]);

  const saveQuizResult = async (result) => {
    if (user && result) {
      // Clear any existing localStorage quiz results for security
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('quiz_result_') || key.startsWith('quiz_completed_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Update state immediately for UI responsiveness
      setQuizResult(result);

      // Save to database only (no localStorage)
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Prepare data for API
          const quizData = {
            primary_milieu_code: result.primary.code,
            primary_milieu_name: result.primary.name,
            primary_milieu_score: result.primary.score,
            primary_milieu_plus3: result.primary.plus3,
            primary_milieu_plus2: result.primary.plus2,
            max_score: result.maxScore,
            ties: result.ties && result.ties.length > 0 ? JSON.stringify(result.ties) : null,
            all_entries: JSON.stringify(result.entries),
            cluster_name: result.cluster?.name || null,
            cluster_description: result.cluster?.description || null,
            cluster_milieus: result.cluster?.milieus ? JSON.stringify(result.cluster.milieus) : null,
            result_data: JSON.stringify(result), // Full result for backward compatibility
          };

          const response = await fetch(`${API_CONFIG.BASE_URL}/quiz-results/save`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(quizData),
          });

          if (!response.ok) {
            console.error('Failed to save quiz result to database:', response.status);
            // Clear state if save failed
            setQuizResult(null);
          }
        }
      } catch (error) {
        console.error('Error saving quiz result to database:', error);
        // Clear state if save failed
        setQuizResult(null);
      }
    }
  };

  const resetProgress = async () => {
    if (user) {
      // Delete quiz result from database
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await fetch(`${API_CONFIG.BASE_URL}/quiz-results/delete`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        }
      } catch (error) {
        console.error('Error deleting quiz result from database:', error);
        // Continue anyway
      }

      // Clear all quiz-related localStorage items for security
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('quiz_result_') || key.startsWith('quiz_completed_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Delete checklist progress from database
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await fetch(`${API_CONFIG.BASE_URL}/checklist-progress/delete`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        }
      } catch (error) {
        console.error('Error deleting checklist progress from database:', error);
        // Continue anyway
      }

      setQuizResult(null);
      
      // Reset checklist to default
      const defaultItems = CHECKLIST_DATA.map(cat => ({
        ...cat,
        items: cat.items.map(item => ({ ...item, completed: false }))
      }));
      setChecklistItems(defaultItems);
    }
  };

  const toggleItem = async (categoryIndex, itemId) => {
    if (!user) return;
    
    setChecklistItems(prev => {
      const updated = prev.map((category, catIdx) => {
        if (catIdx === categoryIndex) {
          return {
            ...category,
            items: category.items.map(item => 
              item.id === itemId 
                ? { ...item, completed: !item.completed }
                : item
            )
          };
        }
        return category;
      });
      
      // Save to database (with current markedAsDone from localStorage if available)
      const saveProgress = async () => {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            // Load markedAsDone from localStorage
            const markedKey = `marked_as_done_${user.id}`;
            const savedMarked = localStorage.getItem(markedKey);
            const markedAsDone = savedMarked ? JSON.parse(savedMarked) : {};
            
            const progressData = {
              checklistItems: updated,
              markedAsDone: markedAsDone
            };
            
            await fetch(`${API_CONFIG.BASE_URL}/checklist-progress/save`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                progress_data: JSON.stringify(progressData)
              }),
            });
          }
        } catch (error) {
          console.error('Error saving checklist progress to database:', error);
        }
      };
      
      saveProgress();
      
      return updated;
    });
  };

  const saveChecklistProgressWithMarkedAsDone = async (markedAsDone) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const progressData = {
          checklistItems: checklistItems,
          markedAsDone: markedAsDone
        };
        
        await fetch(`${API_CONFIG.BASE_URL}/checklist-progress/save`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            progress_data: JSON.stringify(progressData)
          }),
        });
      }
    } catch (error) {
      console.error('Error saving checklist progress to database:', error);
    }
  };

  const getProgressStats = () => {
    const total = checklistItems.reduce((sum, cat) => sum + cat.items.length, 0);
    const completed = checklistItems.reduce((sum, cat) => 
      sum + cat.items.filter(item => item.completed).length, 0
    );
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { total, completed, percentage };
  };

  const getCategoryProgress = () => {
    return checklistItems.map(category => {
      const total = category.items.length;
      const completed = category.items.filter(item => item.completed).length;
      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
      
      return {
        name: category.category,
        total,
        completed,
        percentage
      };
    });
  };


  const saveChecklistProgress = async (items, markedAsDone = {}) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Include markedAsDone in the progress data
        const progressData = {
          checklistItems: items,
          markedAsDone: markedAsDone
        };
        
        await fetch(`${API_CONFIG.BASE_URL}/checklist-progress/save`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            progress_data: JSON.stringify(progressData)
          }),
        });
      }
    } catch (error) {
      console.error('Error saving checklist progress to database:', error);
    }
  };

  const value = {
    quizResult,
    saveQuizResult,
    resetProgress,
    checklistItems,
    toggleItem,
    isLoaded,
    getProgressStats,
    getCategoryProgress,
    saveChecklistProgressWithMarkedAsDone,
  };

  return (
    <ChecklistContext.Provider value={value}>
      {children}
    </ChecklistContext.Provider>
  );
};

export const useChecklist = () => {
  const context = useContext(ChecklistContext);
  if (!context) {
    throw new Error('useChecklist must be used within a ChecklistProvider');
  }
  return context;
};

export default ChecklistContext;

